import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { ANALYTICS_STRINGS } from '@belong/analytics';
import useAnalyticsEvents from '@belong/providers/analyticsEvents';
import type { IEmbeddedInformationModal } from '@belong/types';
import { Button } from '../../../actions/Button';
import { RichText } from '../../../styles/Typography/RichText/RichText';
import { Modal } from '../Modal';

/**
 * The **EmbeddedInformationModal** is a Modal component which is accessible via a Button component.
 *
 * It can only display information and does not provide the buttons/actions of a regular modal.
 *
 * It has been designed to be *embedded* within a RichText.
 */
export const EmbeddedInformationModal: FC<IEmbeddedInformationModal> = ({
  link,
  modalContents,
  image,
  heading
}: IEmbeddedInformationModal) => {
  const analyticsEvents = useAnalyticsEvents();

  const [isOpen, setIsOpen] = useState(false);

  const onClick = () => {
    setIsOpen(!isOpen);

    analyticsEvents.send({
      category: ANALYTICS_STRINGS.CATEGORY.PAGE_ENGAGEMENT,
      action: ANALYTICS_STRINGS.ACTION.OPEN_MODAL,
      text: link.label
    });
  };

  // Add a ThemeProvider to clear possible foregroundColor and backgroundColor set from feedback/Alert
  return (
    <>
      <Button type="button" {...link} onClick={onClick} />
      {isOpen &&
        createPortal(
          <Modal
            isOpen={isOpen}
            ariaLabelClose="Close modal"
            onClose={() => setIsOpen(false)}
            heading={heading}
            content={
              modalContents && <RichText html={modalContents} h2="headingMedium" h3="headingSmall" h4="headingXSmall" />
            }
            image={image}
          />,
          document.body
        )}
    </>
  );
};

EmbeddedInformationModal.displayName = 'EmbeddedInformationModal';
